import type { Footprint, WorldPoint } from '../types.js';

export interface ScreenOffset {
    sx: number;
    sy: number;
}

export interface FootprintSize {
    width: number;
    height: number;
}

interface IsoOptions {
    tileWidth?: number;
    tileHeight?: number;
    cellSize?: number;
    heightUnit?: number | null;
}

export class IsoProjection {
    readonly tileWidth: number;
    readonly tileHeight: number;
    readonly cellSize: number;
    readonly heightUnit: number;

    private readonly hw: number;
    private readonly hh: number;

    constructor({
        tileWidth = 64,
        tileHeight = 32,
        cellSize = 1,
        heightUnit = null,
    }: IsoOptions = {}) {
        this.tileWidth = tileWidth;
        this.tileHeight = tileHeight;
        this.cellSize = cellSize;

        this.hw = tileWidth / (2 * cellSize);
        this.hh = tileHeight / (2 * cellSize);

        this.heightUnit = heightUnit ?? tileHeight;
    }

    worldToScreen(x: number, z: number): ScreenOffset {
        return {
            sx: (x - z) * this.hw,
            sy: (x + z) * this.hh,
        };
    }

    screenToWorld(sx: number, sy: number): WorldPoint {
        const u = sx / this.hw;
        const v = sy / this.hh;
        return {
            x: (u + v) / 2,
            z: (v - u) / 2,
        };
    }

    footprintSize(footprint: Footprint = { w: 1, h: 1 }): FootprintSize {
        const span = footprint.w + footprint.h;
        return {
            width: (span * this.tileWidth) / 2,
            height: (span * this.tileHeight) / 2,
        };
    }

    heightToPixels(height: number): number {
        return height * this.heightUnit;
    }
}
